/** @file RigEntryFields: one facet entry's anchor (px), tile size (px), rotate rule and cut, typed. */
import type { PuppetInputs } from '@zilo/pose3d-commands/types/puppetInputs';
import type { CutMode, FacetEntry, RotateMode } from '@zilo/pose3d-format/types/facet';
import type { ReactElement, ReactNode } from 'react';
import type { QuadOverride } from '../../state/editorTypes';
import type { ResizeEnv } from '../art/fit/applyEntryResize';
import { anchorLabel } from './anchorLabel';
import { anchorPx } from './anchorPx';
import { commitRig } from './commitRig';
import { typedResize } from './typedResize';
import { VecInput } from './VecInput';

/** The fields `facet.setEntry` merges. */
type EntryFields = PuppetInputs['facet.setEntry']['fields'];

/**
 * The entry `index` of drawable `id` on `node` (parent `parent`), the override to show live (or null), the resize path,
 * and `children` (the drawable's Offset row) placed between Anchor and Size.
 */
export type RigEntryFieldsProps = {
  id: string; index: number; e: FacetEntry; node: string; parent: string | undefined; live: QuadOverride | null; env: ResizeEnv; children?: ReactNode;
};

const row = { display: 'flex', alignItems: 'center', gap: 6 };
const label = { width: 92, color: 'var(--muted, #8b919e)' };
const note = { paddingLeft: 96, fontSize: 10, color: 'var(--p3-muted, #8b919e)', overflowWrap: 'anywhere' } as const;
const ROTATE: RotateMode[] = ['none', 'screen', 'parent', 'ground'];
const CUT: CutMode[] = ['cut', 'scale', 'none'];
const XY = ['X', 'Y'] as const;
const WH = ['W', 'H'] as const;

/** Half-pixel display, two decimals for the fraction (stored values are never rounded). */
const px = (n: number): number => Math.round(n * 2) / 2;
const frac = (n: number): string => (Math.round(n * 100) / 100).toString();

/**
 * Anchor (the pivot pixel, half px, clamped to the tile; the fraction as a muted readout; which joint it sits on) and
 * Size (whole px, the edge away from the anchor moves) go through `commitRig` (drawn at once, then one batch, the fit
 * prompt when the entry holds images); while a handle drag runs (`live`) both show its values. Rotate and Cut are
 * one `facet.setEntry` each.
 */
export function RigEntryFields(props: RigEntryFieldsProps): ReactElement {
  const { id, index, e, live, env } = props;
  const docPivot = anchorPx(e.size, e.pivotFrac);
  const size = live?.size ?? e.size;
  const pivot = live?.pivot ?? docPivot;
  const set = (fields: EntryFields): void => { void env.store.getState().dispatch([{ name: 'facet.setEntry', input: { drawable: id, entry: index, fields } }], `set ${id}[${index}]`); };
  const land = (next: { size: readonly [number, number]; pivot: readonly [number, number] }): void => {
    void commitRig(env, { drawable: id, entry: index, size0: e.size, pivot0: docPivot, size: next.size, pivot: next.pivot });
  };
  return (
    <>
      <VecInput label="Anchor" unit="px" axes={XY} value={[px(pivot[0]), px(pivot[1])]} step={0.5} muted={false}
        onCommit={(next, axis) => { land({ size: e.size, pivot: anchorPx(e.size, e.pivotFrac, { axis: axis === 0 ? 0 : 1, px: next[axis] ?? 0 }) }); }} />
      <div data-anchor={id} style={note}>
        {anchorLabel(e.rotate, props.node, props.parent)}
        <span data-frac={id} style={{ marginLeft: 6, opacity: 0.8 }}>frac {frac(pivot[0] / size[0])}, {frac(pivot[1] / size[1])}</span>
      </div>
      {props.children}
      <VecInput label="Size" unit="px" axes={WH} value={[size[0], size[1]]} step={1}
        onCommit={(next) => { land(typedResize({ size: e.size, pivot: docPivot }, [next[0] ?? e.size[0], next[1] ?? e.size[1]])); }} />
      <label style={row}><span style={label}>rotate</span>
        <select aria-label="rotate" value={e.rotate} onChange={(ev) => { set({ rotate: ev.currentTarget.value as RotateMode }); }}>
          {ROTATE.map((r) => <option key={r} value={r}>{r}</option>)}
        </select></label>
      <label style={row}><span style={label}>cut</span>
        <select aria-label="cut" value={e.cut} onChange={(ev) => { set({ cut: ev.currentTarget.value as CutMode }); }}>
          {CUT.map((c) => <option key={c} value={c}>{c}</option>)}
        </select></label>
      <label style={row}><span style={label}>cut inverse</span>
        <input type="checkbox" checked={e.cutInverse === true} onChange={(ev) => { set({ cutInverse: ev.currentTarget.checked }); }} /></label>
    </>
  );
}
